import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { alpha, Checkbox, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import { makeStyles } from '@mui/styles'
import type {
  ProviderUpdateType,
  SetProvidersUpdate,
} from '@app/src/components/ManageProviderDrawer/CategoryDrawer/CategoryDrawerContent'
import type { Dispatch, SetStateAction } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import type { StandardCategory } from '@app/src/types/categories'
import { ProviderStandardCategoryNames } from '@app/src/types/categories'
import { capitalize } from '@app/src/utils/helpers'
import {
  countProvidersWithOwner,
  isOwnerOptionChecked,
  shouldConfirmSelfOwnerRemoval,
} from '@app/src/utils/providerOwnerUsersUtils'
import { ResourceTypes } from '@app/src/wf-constants'
import { useAccount } from '@app/src/context/AccountContext'
import { useConfirm } from 'material-ui-confirm'
import { useOwnerRestriction } from '@app/src/hooks/useOwnerRestriction'

const useStyles = makeStyles(({ palette, spacing }) => ({
  iconSelected: {
    color: palette.primary.main,
  },
  icon: {
    color: alpha(palette.primary.main, 0.54),
  },
  row: {
    '&:hover': {
      backgroundColor: alpha(palette.primary.main, 0.05),
    },
  },
  paddingLeft: {
    paddingLeft: spacing(1),
  },
  checkBox: {
    marginRight: 8,
  },
}))

interface CategoryOptionListItemProps {
  option: string | number
  providersUpdate?: ProviderUpdateType[]
  setProvidersUpdate?: SetProvidersUpdate
  setIsUnsavedChanges: Dispatch<SetStateAction<boolean>>
  category: StandardCategory
  renderPrimaryText?: (text: string, option: string | number) => string | JSX.Element
}

const StandardCategoryListItem = ({
  option,
  providersUpdate,
  category,
  setIsUnsavedChanges,
  setProvidersUpdate,
  renderPrimaryText: renderStylingForItemPrimaryText,
}: CategoryOptionListItemProps): JSX.Element => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const { account } = useAccount()
  const confirm = useConfirm()
  const { accountIsOwnerRestricted } = useOwnerRestriction()

  const isOwnerUsers = category.name === ProviderStandardCategoryNames.OwnerUserIds
  const categoryName = `${category?.name}` as keyof ProviderUpdateType

  // Checkbox checked state
  const checked = (() => {
    if (!isOwnerUsers) {
      return (
        providersUpdate?.every(p => p[categoryName] === option || (p[categoryName] === undefined && option === 0)) ??
        false
      )
    }
    return isOwnerOptionChecked(providersUpdate ?? [], option)
  })()

  // Count of providers with this option applied
  const appliedToNProviders = (() => {
    if (!providersUpdate?.length) return 0
    if (!isOwnerUsers) {
      return providersUpdate.filter(p => p[categoryName] === option || (p[categoryName] === undefined && option === 0))
        .length
    }
    return countProvidersWithOwner(providersUpdate, Number(option))
  })()

  const appliedToSomeProviders = appliedToNProviders > 0 && appliedToNProviders < (providersUpdate?.length ?? 0)

  const getTranslatedCategoryOption = () => {
    if (isOwnerUsers) return ''
    return typeof option === 'number'
      ? formatMessage({ id: `schemas.provider.${category?.name}Values` }, { tier: option })
      : formatMessage({ id: `schemas.provider.${category?.name}Values.${option}` })
  }

  const translatedCategoryOption = getTranslatedCategoryOption()
  const primaryText = renderStylingForItemPrimaryText
    ? renderStylingForItemPrimaryText(translatedCategoryOption, option)
    : translatedCategoryOption

  const handleOnClick = async () => {
    if (!setProvidersUpdate) return

    // Click-time confirmation for owner-restricted users: if this toggle would remove
    // the current user (either by unselecting their own ID or by clearing all owners),
    // prompt the user before staging the change.
    if (isOwnerUsers && typeof option === 'number') {
      const needsConfirm = shouldConfirmSelfOwnerRemoval(
        Number(option),
        providersUpdate ?? [],
        account?.user?.id,
        accountIsOwnerRestricted,
      )
      if (needsConfirm) {
        try {
          const affectedCount = (providersUpdate ?? []).length
          await confirm({
            title: formatMessage({ id: 'general.areYouSure' }),
            description: formatMessage({ id: 'schemas.provider.ownerRemoval.confirmation' }, { count: affectedCount }),
          })
        } catch {
          return
        }
      }
    }

    setIsUnsavedChanges(true)
    setProvidersUpdate(category.name, option)
  }

  return (
    <ListItem dense key={option.toString()} disableGutters className={classes.row} onClick={handleOnClick} button>
      <ListItemIcon>
        <Checkbox
          className={classes.checkBox}
          {...(!isOwnerUsers
            ? {
                icon: <RadioButtonUncheckedIcon className={classes.icon} />,
                checkedIcon: <RadioButtonCheckedIcon className={classes.iconSelected} />,
              }
            : {})}
          checked={checked}
        />
      </ListItemIcon>
      <ListItemText
        className={classes.paddingLeft}
        primary={primaryText}
        secondary={
          appliedToSomeProviders &&
          formatMessage(
            { id: 'category.categoryOptions.appliedToSome' },
            {
              provider: formatMessage(
                { id: `general.${capitalize(ResourceTypes.Company)}` },
                { count: appliedToNProviders },
              ).toLowerCase(),
              amount: appliedToNProviders,
            },
          )
        }
      />
    </ListItem>
  )
}

export default StandardCategoryListItem
